<template>
  <div id="app-root">
    <template v-if="isPublicRoute">
      <router-view />
    </template>
    <template v-else>
      <div class="layout">
        <!-- Mobile top bar (only < 768px): hamburger opens the sidebar drawer -->
        <header class="mobile-topbar">
          <button class="hamburger" :aria-label="'menu'" @click="mobileNavOpen = true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
          <span class="mt-brand">
            <svg width="22" height="22" viewBox="0 0 36 36" fill="none"><rect width="36" height="36" rx="8" fill="var(--accent)"/><path d="M10 14h16M10 18h10M10 22h13" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/></svg>
            DevTask
          </span>
        </header>
        <div v-if="mobileNavOpen" class="nav-backdrop" @click="mobileNavOpen = false"></div>
        <AppSidebar :mobile-open="mobileNavOpen" @logout="handleLogout" />
        <main class="main-content">
          <AppTopbar />
          <div class="wrap">
            <router-view />
          </div>
        </main>
      </div>
    </template>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppSidebar from '@/components/AppSidebar.vue'
import AppTopbar from '@/components/AppTopbar.vue'
import { useAuth } from '@/composables/useAuth'
import { initRealtime, teardownRealtime } from '@/composables/useRealtime'
import api from '@/api'

export default {
  name: 'App',
  components: { AppSidebar, AppTopbar },
  setup() {
    const route  = useRoute()
    const router = useRouter()
    const { clearSession, currentUser, state } = useAuth()

    const isPublicRoute = computed(() => route.meta.public)

    // Mobile nav drawer — close on any navigation.
    const mobileNavOpen = ref(false)
    watch(() => route.fullPath, () => { mobileNavOpen.value = false })

    // Connect realtime when logged in (internal users have an office_id).
    function connectRealtime() {
      const u = currentUser()
      if (u && u.office_id) initRealtime(u.office_id)
    }
    if (state.token) connectRealtime()
    watch(() => state.token, (tok) => { tok ? connectRealtime() : teardownRealtime() })

    async function handleLogout() {
      try { await api.logout() } catch {}
      teardownRealtime()
      clearSession()
      router.push({ name: 'login' })
    }

    return { isPublicRoute, handleLogout, mobileNavOpen }
  },
}
</script>

<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* DevTask design system — adopts the shared design language (design/DESIGN_SYSTEM.md):
   calm white canvas, hairline borders, ONE deep-navy accent, soft pastel status,
   deep glassy floating sidebar, big-light numbers, Assistant font, RTL-first.
   Tokens use DevTask's own names (no external CSS imported). */
:root {
  --bg:        #FEFEFE;
  --surface:   #FFFFFF;
  --surface-2: #F6F7F9;
  --surface-3: #EEF0F4;
  --border:    #C5CCD6;
  --border-2:  #A6AFBC;
  /* card/panel edge — border with a faint brand tint so boxes read on white */
  --card-brd:  #B9C2CE;
  --text:      #1B1C20;
  --muted:     #6A7180;
  --faint:     #9AA1AE;
  /* NVIDIA green — the one brand accent (black text on green) */
  --accent:    #76B900;
  --accent-h:  #5A8D00;
  --accent-ink:#4F7A00;
  --accent-soft: #EAF5CC;
  --accent-wash: rgba(118,185,0,.10);
  --on-accent: #000000;
  --primary:   #76B900;
  --primary-h: #5A8D00;
  /* soft status (pastel bg + darker fg) */
  --st-green-bg:#E5F5ED; --st-green-fg:#1E8A53;
  --st-amber-bg:#FBEFD7; --st-amber-fg:#B47514;
  --st-red-bg:#FCE8E8;   --st-red-fg:#D14444;
  --st-gray-bg:#EEF0F3;  --st-gray-fg:#6A7180;
  --success:   #1E8A53;
  --warning:   #B47514;
  --danger:    #D14444;
  --low:       #9AA1AE;
  --normal:    #5B6472;
  --high:      #B47514;
  --critical:  #D14444;
  --hover:     #F3F4F7;
  --badge-bg:  #EEF0F3;
  --radius:    10px;
  --radius-sm: 8px;
  --shadow:    0 10px 30px rgba(20,24,40,.08);
  --font:      'Rubik', 'Assistant', system-ui, -apple-system, sans-serif;
  --font-mono: 'Rubik', 'Assistant', system-ui, sans-serif;
  /* NVIDIA black chrome sidebar (flat, green active) — black in both themes */
  --sb-bg:#0B0B0C; --sb-bg-2:#000000; --sb-ink:#FFFFFF; --sb-ink-2:rgba(255,255,255,.62);
  --sb-icon:#76B900; --sb-hover:rgba(255,255,255,.07); --sb-active:#76B900;
  --sb-divider:rgba(255,255,255,.12);
}

[data-theme="dark"] {
  --bg:        #19191B;
  --surface:   #232327;
  --surface-2: #1F1F22;
  --surface-3: #2B2B30;
  --border:    #3D3D45;
  --border-2:  #50505B;
  --card-brd:  #4A4A55;
  --text:      #F2F2F4;
  --muted:     #A2A2AC;
  --faint:     #6E6E78;
  --accent:    #8BD000;
  --accent-h:  #A4E61F;
  --accent-ink:#A4E61F;
  --accent-soft: #1E2A0C;
  --accent-wash: rgba(139,208,0,.16);
  --on-accent: #000000;
  --primary:   #8BD000;
  --primary-h: #A4E61F;
  --st-green-bg:#163325; --st-green-fg:#5FCB8E;
  --st-amber-bg:#3A2C14; --st-amber-fg:#E0A94B;
  --st-red-bg:#3A1E1E;   --st-red-fg:#ED7878;
  --st-gray-bg:#2C2C32;  --st-gray-fg:#9AA1AE;
  --success:   #5FCB8E;
  --warning:   #E0A94B;
  --danger:    #ED7878;
  --low:       #6E6E78;
  --normal:    #7C8696;
  --high:      #E0A94B;
  --critical:  #ED7878;
  --hover:     #2A2A2F;
  --badge-bg:  #2C2C32;
  --shadow:    0 14px 44px rgba(0,0,0,.5);
}

body {
  background: var(--bg);
  color: var(--text);
  font-family: var(--font);
  font-size: 14px;
  line-height: 1.5;
  min-height: 100vh;
}

/* Force LTR flex flow so the sidebar is ALWAYS physically on the left,
   regardless of UI language. Content direction is set per-locale below. */
.layout {
  display: flex;
  direction: ltr;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 28px 32px;
  min-width: 0;
  direction: rtl;            /* Hebrew default */
}
html[dir="ltr"] .main-content { direction: ltr; }
/* Sidebar stays left, but its text/icons follow the UI language. */
.sidebar { direction: rtl; }
html[dir="ltr"] .sidebar { direction: ltr; }

/* shared utility classes */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 15px;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  cursor: pointer;
  font-family: var(--font);
  font-size: 13px;
  font-weight: 600;
  transition: background .15s, color .15s, border-color .15s;
}
.btn:disabled { opacity: .45; cursor: not-allowed; }
.btn-primary   { background: var(--accent); color: var(--on-accent); }
.btn-primary:hover:not(:disabled) { background: var(--accent-h); }
.btn-success   { background: var(--success); color: #fff; }
.btn-danger    { background: var(--danger);  color: #fff; }
.btn-ghost     { background: transparent; color: var(--muted); border-color: var(--border-2); }
.btn-ghost:hover { color: var(--text); border-color: var(--accent); }
.btn-sm        { padding: 4px 10px; font-size: 12px; }
.btn-icon      { padding: 6px; border-radius: var(--radius-sm); }

/* Soft status pills — pastel bg + darker same-hue text, no border, rounded. */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  border: none;
}
.badge-low         { background: var(--st-gray-bg);  color: var(--st-gray-fg); }
.badge-normal      { background: var(--accent-soft); color: var(--accent); }
.badge-high        { background: var(--st-amber-bg); color: var(--st-amber-fg); }
.badge-critical    { background: var(--st-red-bg);   color: var(--st-red-fg); }
.badge-open        { background: var(--st-gray-bg);  color: var(--st-gray-fg); }
.badge-in_progress { background: var(--st-amber-bg); color: var(--st-amber-fg); }
.badge-done        { background: var(--st-green-bg); color: var(--st-green-fg); }

/* tabular figures for numbers/metrics */
.num { font-feature-settings: "tnum"; font-variant-numeric: tabular-nums; }

.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 16px; color: var(--text); margin-bottom: 7px; font-weight: 600; }
.form-control {
  width: 100%; padding: 9px 12px;
  background: var(--surface); border: 1px solid var(--border-2);
  border-radius: var(--radius-sm); color: var(--text);
  font-family: var(--font);
  font-size: 14px; outline: none;
  transition: border-color .15s;
}
.form-control:focus { border-color: var(--accent); }
.form-control::placeholder { color: var(--muted); }

.card {
  background: var(--surface);
  border: 1px solid var(--border-2);
  border-radius: var(--radius);
  padding: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}
.page-title { font-size: 30px; font-weight: 800; letter-spacing: -.02em; line-height: 1.2; }

.spinner {
  width: 32px; height: 32px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin .7s linear infinite;
  margin: 60px auto;
}
@keyframes spin { to { transform: rotate(360deg); } }

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--muted);
}
.empty-state p { margin-top: 8px; font-size: 13px; }

/* Green-on-white thin text fails contrast — links read in ink, accent is for fills/active. */
a { color: var(--text); text-decoration: none; }
a:hover { color: var(--accent-ink); }

select.form-control option { background: var(--surface); }

/* ── RTL adjustments (Hebrew) ───────────────────────────────────────── */
html[dir="rtl"] body { text-align: right; }
html[dir="rtl"] .nav-item { text-align: right; }
/* Sidebar left-pin is handled by .layout{direction:ltr} above (language-stable). */
html[dir="rtl"] .clients-table th { text-align: right; }
html[dir="rtl"] .filter-controls { margin-left: 0; margin-right: auto; }
html[dir="rtl"] .arch-date { margin-left: 0; margin-right: auto; }
html[dir="rtl"] .urgency-stripe,
html[dir="rtl"] .arch-stripe,
html[dir="rtl"] .project-color { order: 2; }

/* ── Mobile: off-canvas sidebar drawer (< 768px) ─────────────────────── */
.mobile-topbar { display: none; }
.nav-backdrop  { display: none; }

@media (max-width: 768px) {
  .layout { display: block; }
  .main-content { padding: 16px; }

  .mobile-topbar {
    display: flex; align-items: center; gap: 12px;
    position: sticky; top: 0; z-index: 30;
    height: 56px; padding: 0 14px;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
  }
  .hamburger { border: none; background: none; cursor: pointer; color: var(--text); display: flex; padding: 6px; }
  .mt-brand { display: flex; align-items: center; gap: 8px; font-weight: 800; font-size: 16px; color: var(--text); }

  .nav-backdrop {
    display: block; position: fixed; inset: 0;
    background: rgba(15,20,40,.45); z-index: 40;
  }
  /* The sidebar's own off-canvas drawer styles live in AppSidebar (scoped),
     so they win the cascade over the component's base .sidebar rules. */
}
</style>
